// src/components/MangaList.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://new-nightlife.jp';

const mangaImages = [
  { baseName: 'manga1', alt: '週5バイトしても生活が厳しい大学生｜夜職・キャバクラ求人に興味を持つ女性', w: 1080, h: 1080 },
  { baseName: 'manga2', alt: 'スマホでキャバクラ求人を探す未経験の女子｜夜職デビュー前', w: 1080, h: 1080 },
  { baseName: 'manga3', alt: '未経験でも高収入と知り驚く女性｜体験入店を考えるキャバ嬢予備軍', w: 1080, h: 1080 },
  { baseName: 'manga4', alt: '夜職で余裕ある生活を実感し笑顔のキャバ嬢｜未経験スタート成功例', w: 1080, h: 1080 },
];

const abs = (p) => (p?.startsWith('http') ? p : `${SITE}${p.startsWith('/') ? '' : '/'}${p}`);

const MangaList = () => {
  const imgRefs = useRef([]);
  const [visible, setVisible] = useState(() => mangaImages.map((_, i) => i === 0)); // 1枚目は最初から見えてOK

  // ---- IOでフェードアップ（見えるのがデフォルト。非表示にしない）----
  useEffect(() => {
    // Safari対策：IOがあっても発火しないケース用の安全タイマー
    const safety1 = setTimeout(() => {
      // 1.5sで最低1枚は確実に「アニメ済み」状態に
      setVisible((prev) => prev.map((v, i) => v || i === 0));
    }, 1500);
    const safety2 = setTimeout(() => {
      // 2sで全表示（IOが死んでても見える）
      setVisible(mangaImages.map(() => true));
    }, 2000);

    if (!('IntersectionObserver' in window)) {
      // 古いSafari等：即全表示
      clearTimeout(safety1);
      clearTimeout(safety2);
      setVisible(mangaImages.map(() => true));
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = Number(e.target.getAttribute('data-index'));
            if (!Number.isNaN(idx)) {
              setVisible((prev) => {
                if (prev[idx]) return prev;
                const next = [...prev];
                next[idx] = true;
                return next;
              });
              obs.unobserve(e.target);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '200px 0px',     // 余裕を増やす
        threshold: 0.1,              // 発火しやすく
      }
    );

    imgRefs.current.forEach((el) => el && obs.observe(el));

    return () => {
      clearTimeout(safety1);
      clearTimeout(safety2);
      obs.disconnect();
    };
  }, []);

  const articleJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE}/` },
    headline: '漫画でわかる未経験キャバクラデビュー',
    image: mangaImages.map((i) => ({
      '@type': 'ImageObject',
      url: abs(`/images/manga/${i.baseName}.webp`),
      width: i.w,
      height: i.h,
    })),
    datePublished: '2024-04-01',
    dateModified: '2025-08-11',
    author: { '@type': 'Organization', name: 'New-Nightlife 編集部' },
    publisher: {
      '@type': 'Organization',
      name: 'New-Nightlife',
      logo: { '@type': 'ImageObject', url: abs('/apple-touch-icon.png'), width: 180, height: 180 },
    },
    description: '漫画でキャバクラ未経験でも安心してスタートできる方法をわかりやすく解説します。',
  }), []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <section style={styles.container} aria-labelledby="manga-title">
        <h2 id="manga-title" className="manga-title">漫画でわかる夜職デビュー</h2>

        {mangaImages.map((img, index) => {
          const webp = `/images/manga/${img.baseName}.webp`;
          const png  = `/images/manga/${img.baseName}.png`;
          const eager = index === 0;

          return (
            <picture key={img.baseName} style={styles.picture}>
              <source srcSet={webp} type="image/webp" />
              <img
                ref={(el) => (imgRefs.current[index] = el)}
                data-index={index}
                src={png}
                alt={img.alt}
                loading={eager ? 'eager' : 'lazy'}
                fetchPriority={eager ? 'high' : 'low'}
                decoding="async"
                width={img.w}
                height={img.h}
                sizes="(max-width: 480px) 100vw, 480px"
                style={{
                  ...styles.image,
                  // 非表示にしない。見えていて、可視化時だけ軽くアニメ
                  transform: visible[index] ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.98)',
                  opacity: 1,
                  transition: 'transform .6s ease',
                }}
              />
            </picture>
          );
        })}
      </section>

      <style>{`
        .manga-title {
          font-size: 18px; font-weight: 700; text-align: center;
          padding: 21px; margin: 5px 0 12px; color: #e91e63;
          background: linear-gradient(90deg, #ffe0ec 0%, #fcb6d3 50%, #ffe0ec 100%);
          border-radius: 9999px; box-shadow: 0 2px 6px rgba(0,0,0,0.05);
        }
        @media (prefers-reduced-motion: reduce) {
          img[decoding="async"] { transition: none !important; }
        }
      `}</style>
    </>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
    margin: '0 auto',
    width: '100%',
    maxWidth: 480,
  },
  picture: { display: 'block' },
  image: {
    width: '100%',
    height: 'auto',
    // iOSの古いSafariで aspect-ratio が効かない保険 → width/height属性で縦横比を確保済み
    borderRadius: 12,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    display: 'block',
    backgroundColor: '#fff0f5',
  },
};

export default MangaList;
